import { useCallback, useMemo } from 'react';
import { ValidationError, fetchData } from 'utils';
import {
  isAlreadyExistErrorMsg,
  routesWithAlreadyExistErrMsgManaged,
} from 'utils/errorMessageValidation';
import useError from 'hooks/useError';
import useGACookies from 'hooks/webapi/useGACookies';
import useSmartlook from 'hooks/useSmartlook';

type HouseholdOptions = {
  body: {
    numberOfInhabitants: number;
    numberOfAdults: number;
    ownership?: string;
  };
};

export type Consent = {
  name: string;
  accepted: boolean;
  version?: number;
};

type ClientInfo = {
  body: {
    personalId: string;
    firstName: string;
    lastName: string;
    mobilePhone: string;
    email: string;
    password: string;
    acceptAgreement: boolean;
    acceptProfiling: boolean;
    acceptFaceIdProcessing: boolean;
    consents: Consent[];
  };
};

// TODO: Refactor to react-query
const useRegistration = () => {
  const { showError } = useError();
  const { error } = useSmartlook();
  const { getAnalyticsCookies } = useGACookies();

  const partialRegister = useCallback(
    async ({ body }: ClientInfo /* recaptchaToken?: string */) => {
      const gaCookies = getAnalyticsCookies();

      try {
        const data = await fetchData(
          '/registration/partial',
          {
            method: 'post',
            body: JSON.stringify({
              ...body,
              analyticsCookies: gaCookies.length > 0 ? gaCookies : undefined,
            }),
          },
          // { responseToken: recaptchaToken }
        );

        return data;
      } catch (e) {
        error('[useRegistration][partialRegister]', e);
        if (e instanceof ValidationError) {
          const isAlreadyExistErrorFields = isAlreadyExistErrorMsg(e);
          if (
            !(
              isAlreadyExistErrorFields &&
              routesWithAlreadyExistErrMsgManaged.includes(
                window.location.pathname,
              )
            )
          ) {
            showError();
          }
        } else {
          showError();
        }
        throw e;
      }
    },
    [showError, getAnalyticsCookies],
  );

  const putEmployment = useCallback(
    async (employmentStatus: string) => {
      try {
        const data = await fetchData('/client/employment', {
          method: 'put',
          body: JSON.stringify({ employmentStatus }),
        });

        return data;
      } catch (e) {
        error('[useRegistration][putEmployment]', e);
        if (e instanceof ValidationError) {
          showError();
          throw e;
        }
        // Don't throw error otherwise
        return undefined;
      }
    },
    [showError],
  );
  const putHousehold = useCallback(
    async ({ body }: HouseholdOptions): Promise<void> => {
      try {
        const data = await fetchData('/client/household', {
          method: 'put',
          body: JSON.stringify(body),
        });

        return data;
      } catch (e) {
        error('[useRegistration][putHousehold]', e);
        if (e instanceof ValidationError) {
          showError();
          throw e;
        }
        // Don't throw error otherwise
        return undefined;
      }
    },
    [showError],
  );

  return useMemo(() => {
    return {
      putHousehold,
      putEmployment,
      partialRegister,
    };
  }, [putHousehold, putEmployment, partialRegister]);
};

export default useRegistration;
